import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input.jsx';
import { searchCrmContacts } from '@/services/licenses.service.js';

// Debounced CRM Contact combobox. Calls the License Manager's _crm-contacts
// search (same backend as admin-carriers) unless a `searchFn(q)` override is
// supplied (the carrier admin passes its own endpoint). On select, fires
// onSelect({ id, name, email }). Controlled by `value` = { id, name } | null.
export default function CrmContactPicker({ value, onSelect, placeholder = 'Search CRM contacts…', searchFn = searchCrmContacts }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function handleQuery(val) {
    setQuery(val);
    setOpen(true);
    clearTimeout(debounceRef.current);
    if (val.trim().length >= 2) {
      debounceRef.current = setTimeout(async () => {
        setSearching(true);
        try {
          const rows = await searchFn(val.trim());
          setResults(Array.isArray(rows) ? rows : []);
        } catch { setResults([]); }
        finally { setSearching(false); }
      }, 300);
    } else {
      setResults([]);
    }
  }

  function pick(r) {
    onSelect({ id: r.id, name: r.Full_Name, email: r.Email });
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  if (value?.id) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-card border border-border bg-surface px-3 py-2 text-sm">
        <span className="truncate text-text">{value.name || value.id}</span>
        <button type="button" onClick={() => onSelect(null)} className="shrink-0 text-text-dim hover:text-text" aria-label="Clear">
          <X size={15} strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
        <Input className="pl-9" placeholder={placeholder} value={query} onChange={(e) => handleQuery(e.target.value)} onFocus={() => setOpen(true)} />
      </div>
      {open && (query.trim().length >= 2) && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-card border border-border bg-surface shadow-lg">
          {searching && <div className="px-3 py-2 text-xs text-text-muted">Searching…</div>}
          {!searching && results.length === 0 && <div className="px-3 py-2 text-xs text-text-muted">No matches</div>}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => pick(r)}
              className="flex w-full flex-col items-start border-b border-border px-3 py-2 text-left last:border-0 hover:bg-surface-strong/50"
            >
              <span className="text-sm text-text">{r.Full_Name || '(no name)'}</span>
              <span className="text-xs text-text-dim">{[r.Email, r.Account_Name].filter(Boolean).join(' · ') || r.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
